<script setup>
import { ref, computed, onMounted } from 'vue';
import { useUserStore } from '@/store/user-store';
import GlobalInvestQuestionnaire from '@/components/global/GlobalInvestQuestionnaire.vue';
import GlobalInvestReview from '@/components/global/GlobalInvestReview.vue';
import GlobalInvestResult from '@/components/global/GlobalInvestResult.vue';

const store = useUserStore();

// Cache keys for localStorage
const ANSWERS_CACHE_KEY = 'global_invest_answers';
const RECOMMENDATION_CACHE_KEY = 'global_invest_recommendation';
const STEP_CACHE_KEY = 'global_invest_step';

const step = ref(1); // 1=questions, 2=review, 3=result
const answers = ref({});
const isLoading = ref(false);
const apiError = ref('');
const recommendation = ref(null);

const canGoBack = computed(() => step.value > 1 && !isLoading.value);

// Load cached data from localStorage
const loadCachedData = () => {
  try {
    const cachedAnswers = localStorage.getItem(ANSWERS_CACHE_KEY);
    const cachedRecommendation = localStorage.getItem(RECOMMENDATION_CACHE_KEY);
    const cachedStep = localStorage.getItem(STEP_CACHE_KEY);

    if (cachedAnswers) {
      answers.value = JSON.parse(cachedAnswers);
    }

    if (cachedRecommendation) {
      recommendation.value = JSON.parse(cachedRecommendation);
    }

    if (cachedStep) {
      step.value = parseInt(cachedStep, 10);
    }

    console.log('Loaded cached global invest data:', {
      hasAnswers: !!cachedAnswers,
      hasRecommendation: !!cachedRecommendation,
      step: step.value
    });
  } catch (error) {
    console.error('Error loading cached global invest data:', error);
  }
};

// Save data to localStorage
const saveCachedData = () => {
  try {
    if (Object.keys(answers.value).length > 0) {
      localStorage.setItem(ANSWERS_CACHE_KEY, JSON.stringify(answers.value));
    }

    if (recommendation.value) {
      localStorage.setItem(RECOMMENDATION_CACHE_KEY, JSON.stringify(recommendation.value));
    }

    localStorage.setItem(STEP_CACHE_KEY, step.value.toString());
  } catch (error) {
    console.error('Error saving cached global invest data:', error);
  }
};

// Clear all cached data (for "Start Fresh" functionality)
const clearCachedData = () => {
  localStorage.removeItem(ANSWERS_CACHE_KEY);
  localStorage.removeItem(RECOMMENDATION_CACHE_KEY);
  localStorage.removeItem(STEP_CACHE_KEY);
};

const handleQuestionnaireComplete = (payload) => {
  answers.value = payload;
  step.value = 2;
  saveCachedData();
};

const handleEditAnswers = () => {
  step.value = 1;
  saveCachedData();
};

const handleSubmit = async () => {
  apiError.value = '';
  isLoading.value = true;
  try {
    const resp = await store.getGlobalInvestOptions(answers.value);
    recommendation.value = resp;
    step.value = 3;
    saveCachedData();
  } catch (e) {
    console.error('Error fetching global investing recommendation', e);
    apiError.value = 'Unable to fetch recommendation right now. Please try again.';
  } finally {
    isLoading.value = false;
  }
};

const handleStartFresh = () => {
  answers.value = {};
  recommendation.value = null;
  step.value = 1;
  apiError.value = '';
  clearCachedData();
};

onMounted(() => {
  loadCachedData();
});
</script>

<template>
  <div class="gi-recommender-page">
    <div class="page-body">
      <div class="card shell">
        <div class="shell-header">
          <div class="steps">
            <div :class="['step-pill', { active: step === 1 }]">1. Questionnaire</div>
            <div :class="['step-pill', { active: step === 2 }]">2. Review</div>
            <div :class="['step-pill', { active: step === 3 }]">3. Recommendation</div>
          </div>
          <div class="header-actions">
            <button
              v-if="Object.keys(answers).length > 0 || recommendation"
              class="start-fresh-btn"
              @click="handleStartFresh"
              :disabled="isLoading"
            >
              🔄 Start Fresh
            </button>
            <button v-if="canGoBack" class="back-link" @click="step === 3 ? (step = 2) : (step = 1)">
              ← Back
            </button>
          </div>
        </div>

        <div v-if="apiError" class="error-banner">{{ apiError }}</div>

        <div v-if="step === 1" class="shell-content">
          <GlobalInvestQuestionnaire
            :initial-answers="answers"
            :disabled="isLoading"
            @complete="handleQuestionnaireComplete"
          />
        </div>

        <div v-else-if="step === 2" class="shell-content">
          <GlobalInvestReview
            :answers="answers"
            :disabled="isLoading"
            @edit="handleEditAnswers"
            @submit="handleSubmit"
          />
        </div>

        <div v-else-if="step === 3" class="shell-content">
          <GlobalInvestResult
            :answers="answers"
            :result="recommendation"
            :loading="isLoading"
            @edit="handleEditAnswers"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.gi-recommender-page {
  padding: 24px;
}

.page-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card.shell {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 20px 24px;
}

.shell-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.steps {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-pill {
  padding: 6px 10px;
  border-radius: 999px;
  background: #f1f3f5;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6c757d;
}

.step-pill.active {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
}

.back-link {
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 6px 10px;
  border-radius: 6px;
}

.back-link:hover:not(:disabled) {
  background: #eff6ff;
}

.start-fresh-btn {
  border: 1px solid #f59e0b;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.start-fresh-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #fde68a 0%, #fcd34d 100%);
  border-color: #d97706;
}

.start-fresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.shell-content {
  margin-top: 8px;
}

.error-banner {
  background: #ffe3e3;
  border-radius: 6px;
  padding: 8px 10px;
  color: #c92a2a;
  font-size: 0.82rem;
  margin-bottom: 12px;
}

@media (max-width: 768px) {
  .gi-recommender-page {
    padding: 16px;
  }

  .shell-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .header-actions {
    width: 100%;
    justify-content: space-between;
  }

  .start-fresh-btn,
  .back-link {
    font-size: 0.75rem;
    padding: 5px 10px;
  }
}
</style>
